import { Input } from '@/components/ui/input';
import type { MenuItem } from '@/components/layout/sidebar';
import { resourceRegistry } from '@/resources/resource-map';
import { Search, ChevronDown, ArrowLeft, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageSearch } from '@/hooks/usePageSearch';
import { usePageHeader } from '@/contexts/PageHeaderContext';

const getCreatePathForListPath = (listPath?: string) => {
	if (!listPath) return undefined;
	for (const resource of Object.values(resourceRegistry)) {
		if (resource.list?.path === listPath) {
			return resource.create?.path;
		}
	}
	return undefined;
};

interface HeaderSearchProps {
	menuItems: MenuItem[];
	showBack?: boolean;
	onBack?: () => void;
}

export const HeaderSearch: React.FC<HeaderSearchProps> = ({ menuItems, showBack, onBack }) => {
	const { t } = useTranslation('navigation');
	const pageHeader = usePageHeader();
	const location = useLocation();
	const navigate = useNavigate();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [wasKeyboardNavigation, setWasKeyboardNavigation] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const selectedItemRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const {
		searchQuery,
		setSearchQuery,
		selectedIndex,
		setSelectedIndex,
		displayItems,
		filteredItems,
		handleNavigate,
		isShowingRecent,
		searchableItems
	} = usePageSearch({ menuItems });

	// Scroll selected item into view only for keyboard navigation
	useEffect(() => {
		if (selectedItemRef.current && wasKeyboardNavigation) {
			selectedItemRef.current.scrollIntoView({
				block: 'nearest',
				behavior: 'smooth'
			});
			setWasKeyboardNavigation(false);
		}
	}, [selectedIndex, wasKeyboardNavigation]);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			const target = event.target as Element;

			if (!isDropdownOpen || !target) return;

			if (containerRef.current && !containerRef.current.contains(target)) {
				setIsDropdownOpen(false);
				setSearchQuery('');
				inputRef.current?.blur();
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside, true);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside, true);
			};
		}
	}, [isDropdownOpen, setSearchQuery]);

	// Handle navigation with cleanup
	const handleNavigateAndClose = (item: MenuItem) => {
		handleNavigate(item);
		setIsDropdownOpen(false);
		inputRef.current?.blur();
	};

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			setIsDropdownOpen(false);
			setSearchQuery('');
			inputRef.current?.blur();
			return;
		}

		if (!isDropdownOpen || displayItems.length === 0) {
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setWasKeyboardNavigation(true);
				setSelectedIndex(prev =>
					prev < displayItems.length - 1 ? prev + 1 : prev
				);
				break;
			case 'ArrowUp':
				e.preventDefault();
				setWasKeyboardNavigation(true);
				setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
				break;
			case 'Enter':
				e.preventDefault();
				if (displayItems[selectedIndex]) {
					handleNavigateAndClose(displayItems[selectedIndex]);
				}
				break;
		}
	};

	// Handle create button click for a dropdown item
	const handleCreateClick = useCallback((e: React.MouseEvent, item: MenuItem) => {
		e.preventDefault();
		e.stopPropagation();
		const createPath = getCreatePathForListPath(item.path);
		if (!item.path || !createPath) return;
		setIsDropdownOpen(false);
		setSearchQuery('');
		inputRef.current?.blur();
		navigate(item.path);
		window.requestAnimationFrame(() => {
			navigate(createPath);
		});
	}, [navigate, setSearchQuery]);

	// Handle input focus
	const handleFocus = () => {
		setIsDropdownOpen(true);
	};

	// Handle input change
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setIsDropdownOpen(true);
	};

	// Handle button click (toggle dropdown)
	const handleButtonClick = () => {
		if (isDropdownOpen) {
			setIsDropdownOpen(false);
			setSearchQuery('');
			inputRef.current?.blur();
		} else {
			setIsDropdownOpen(true);
			inputRef.current?.focus();
		}
	};

	const showDropdown = isDropdownOpen && displayItems.length > 0;

	// Compute current title from context, route, or matching menu item
	const currentTitle = useMemo(() => {
		// First priority: always use pageHeader context if available (for nested pages like edit/create)
		if (pageHeader.title) {
			return pageHeader.title;
		}

		// Second priority: find matching menu item for current route (list pages)
		const currentPath = location.pathname;
		const matchingItem = searchableItems.find(item => item.path === currentPath);
		if (matchingItem) {
			return t(matchingItem.titleKey);
		}

		// Third priority: try to derive title from route path
		const segments = currentPath.split('/').filter(Boolean);
		if (segments.length > 0) {
			const resourceSegment = segments[0];
			// Try to find a menu item that starts with this resource
			const resourceItem = searchableItems.find(item =>
				item.path?.startsWith(`/${resourceSegment}`)
			);
			if (resourceItem) {
				return t(resourceItem.titleKey);
			}
			// Fallback to formatted resource name
			return resourceSegment
				.replace(/-/g, ' ')
				.replace(/\b\w/g, (l: string) => l.toUpperCase());
		}

		// Final fallback to dashboard
		return t('menu.dashboard');
	}, [pageHeader.title, location.pathname, searchableItems, t]);

	return (
		<div ref={containerRef} className="relative flex items-center">
			{/* Back button + Title (combined clickable area on edit pages) */}
			{showBack && onBack ? (
				<button
					onClick={onBack}
					className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
					title="Go back"
				>
					<ArrowLeft className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors" />
					<h1 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-[#F1F5F9]">
						{currentTitle}
					</h1>
				</button>
			) : (
				<button
					onClick={handleButtonClick}
					className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
					title={t('search.click_to_search', 'Click to search pages')}
				>
					<h1 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-[#F1F5F9]">
						{currentTitle}
					</h1>
					<ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 opacity-70 group-hover:opacity-100 transition-opacity" />
				</button>
			)}


			{/* Dropdown */}
			<AnimatePresence>
				{isDropdownOpen && (
					<motion.div
						initial={{ opacity: 0, y: -8, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: -8, scale: 0.95 }}
						transition={{ duration: 0.15, ease: "easeOut" }}
						className="absolute left-0 right-auto top-full z-50 mt-2 min-w-[280px] w-[min(calc(100vw-2rem),24rem)] overflow-hidden rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-xl"
						style={{ maxWidth: `calc(100vw - ${containerRef.current?.getBoundingClientRect().left ?? 0}px - 1rem)` }}
					>
						{/* Search Input */}
						<div className="p-3 border-b border-gray-200 dark:border-slate-600">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
								<Input
									ref={inputRef}								id="header-search"
								name="header-search"									type="text"
									placeholder={t('search.placeholder')}
									value={searchQuery}
									onChange={handleChange}
									onFocus={handleFocus}
									onKeyDown={handleKeyDown}
									className="w-full pl-9 pr-3 text-sm dark:bg-slate-600 dark:border-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
									autoFocus
								/>
							</div>
						</div>

						{/* Results */}
						{showDropdown && (
							<div ref={dropdownRef} className="max-h-[400px] overflow-y-auto overflow-x-hidden">
								{isShowingRecent && (
									<div className="border-b border-gray-200 dark:border-slate-600 px-3 py-2">
										<span className="text-xs font-medium text-gray-500 dark:text-gray-400">
											{t('search.recent_pages')}
										</span>
									</div>
								)}
								<div className="py-1">
									{displayItems.map((item, index) => {
										const Icon = item.icon;
										const createPath = getCreatePathForListPath(item.path);
										return (
											<motion.div
												key={item.path}
												ref={index === selectedIndex ? selectedItemRef : null}
												initial={{ opacity: 0, x: -8 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{
													delay: index * 0.02,
													duration: 0.15,
													ease: "easeOut"
												}}
												className={`group/item flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors duration-200 min-w-0 ${
													index === selectedIndex
														? 'bg-blue-500 text-white'
														: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600'
												}`}
												onClick={() => handleNavigateAndClose(item)}
												onMouseEnter={() => setSelectedIndex(index)}
												whileHover={{ x: 2 }}
												whileTap={{ scale: 0.98 }}
											>
												<Icon className="h-4 w-4 flex-shrink-0" />
												<span className="flex-1 truncate">{t(item.titleKey)}</span>
												{createPath && (
													<button
														type="button"
														onClick={(e) => handleCreateClick(e, item)}
														className={`group ml-auto inline-flex h-6 w-6 items-center justify-center rounded-md border transition-all duration-200 opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
															index === selectedIndex
																? 'border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/50'
																: 'border-gray-300 dark:border-slate-500/30 bg-gray-100 dark:bg-slate-800/20 text-gray-500 dark:text-slate-300 hover:border-blue-400/50 hover:bg-blue-500/15 hover:text-blue-500 dark:hover:text-blue-300'
														}`}
														aria-label="Create new"
														title="Create new"
													>
														<Plus className="h-3 w-3 transition-transform duration-200 ease-out group-hover:rotate-90 group-hover:scale-110" />
													</button>
												)}
											</motion.div>
										);
									})}
								</div>
							</div>
						)}

						{/* No results message */}
						{isDropdownOpen && searchQuery.trim() && filteredItems.length === 0 && (
							<div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
								{t('search.no_results')}
							</div>
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
